<script setup lang="ts">
// AppHeader — 64px top bar per UI-SPEC §6 and §17.
// Left: "Solvis ER — Futures Dashboard" heading.
// Right: ? page-tour button (only on routes that have a tour) + language switcher
//        + user email + Sign out button.
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { useTourStore } from '@/stores/tour'
import { pageTours, pageTourIdFor } from '@/composables/pageTours'

const { user, logout } = useAuth0()
const route = useRoute()
const tour = useTourStore()

// "Vodič kroz ovu stranicu" — the tour for the current page, if it has one.
const pageTourId = computed(() => pageTourIdFor(route))

function startPageTour() {
    const id = pageTourId.value
    if (id) tour.start(pageTours[id])
}

function signOut() {
    logout({ logoutParams: { returnTo: window.location.origin } })
}
</script>

<template>
    <header
        class="flex h-16 items-center justify-between px-6 py-3"
        style="
            background-color: var(--color-surface-card);
            border-bottom: 1px solid var(--color-border-subtle);
        ">
        <!-- Left: App title -->
        <span class="text-lg font-semibold" style="color: var(--color-ink-strong)">
            {{ $t('appHeader.title') }}
        </span>

        <!-- Right: Page tour + Language switcher + User email + Sign out -->
        <div class="flex items-center gap-4">
            <button
                v-if="pageTourId"
                type="button"
                data-testid="page-tour-btn"
                class="inline-flex h-7 w-7 items-center justify-center rounded-full border text-sm font-semibold transition-colors hover:bg-[var(--color-surface-sunken)]"
                style="color: var(--color-brand-600); border-color: var(--color-border-strong)"
                :title="$t('pageTour.button.label')"
                :aria-label="$t('pageTour.button.label')"
                @click="startPageTour"
            >
                ?
            </button>
            <LanguageSwitcher />
            <span class="text-sm" style="color: var(--color-ink-muted)">
                {{ user?.email ?? '&mdash;' }}
            </span>
            <button
                class="text-sm transition-colors"
                style="color: var(--color-ink-base)"
                @click="signOut">
                {{ $t('appHeader.signOut') }}
            </button>
        </div>
    </header>
</template>
